import { useCallback, useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle } from 'lucide-react';
import type { IDeletePoliciesDialogProps } from '@/@types/insurance';

export function DeletePoliciesDialog({
  isOpen,
  onClose,
  onConfirm,
  count,
}: IDeletePoliciesDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = useCallback(async () => {
    setError(null);
    setIsDeleting(true);
    try {
      await onConfirm();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete policies';
      setError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [onConfirm]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isDeleting) {
        setError(null);
        onClose();
      }
    },
    [isDeleting, onClose]
  );

  const label = count === 1 ? '1 policy' : `${count} policies`;

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-red-100 p-2 dark:bg-red-900/30">
              <AlertTriangle
                aria-hidden="true"
                className="h-5 w-5 text-red-600 dark:text-red-400"
              />
            </div>
            <AlertDialogTitle>Delete {label}?</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2">
            This will permanently delete {label} and all associated drivers and uploaded files. This
            action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && (
          <div
            role="alert"
            className="rounded-md bg-red-50 p-3 text-sm text-red-600 dark:bg-red-950 dark:text-red-400"
          >
            {error}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={handleConfirm} disabled={isDeleting}>
            {isDeleting ? (
              <>
                <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              `Delete ${label}`
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
